import React, { useState } from "react";
import { useBalance } from "../contexts/BalanceContext";
import { usePlanLimits } from "../contexts/PlanLimitContext";
import { useTheme } from "../contexts/ThemeContext";
import { AlertTriangle, X, Plus } from "lucide-react";
import AddFundsModal from "./AddFundsModal";

const LowBalanceBanner = () => {
  const { currentBalance, isLowBalance, loading } = useBalance();
  const { isAtLimit, loading: limitsLoading } = usePlanLimits();
  const { isDark } = useTheme();
  const [dismissed, setDismissed] = useState(false);
  const [showFundsModal, setShowFundsModal] = useState(false);

  if (loading || limitsLoading || dismissed) return null;

  // Only show banner if user has exceeded plan limits (overages apply)
  const hasOverage = isAtLimit("sms") || isAtLimit("minutes");
  if (!hasOverage || !isLowBalance()) return null;

  const isEmpty = currentBalance <= 0;

  return (
    <>
      <div
        className={`mx-4 mb-3 mt-1 rounded-xl p-3 flex items-center gap-3 ${
          isEmpty
            ? 'bg-red-500/10 border border-red-500/20'
            : 'bg-amber-500/10 border border-amber-500/20'
        }`}
        data-testid="low-balance-banner"
      >
        <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${isEmpty ? 'text-red-400' : 'text-amber-400'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${isEmpty ? 'text-red-400' : 'text-amber-400'}`}>
            {isEmpty ? "No funds in your account" : "Low balance warning"}
          </p>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {isEmpty
              ? "You've exceeded your plan limits. Add funds to continue using SMS and call services."
              : `Balance: $${currentBalance.toFixed(2)}. You're over your plan limits — add funds to avoid service interruption.`}
          </p>
        </div>
        <button
          onClick={() => setShowFundsModal(true)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FFCC00] text-black text-xs font-bold whitespace-nowrap hover:brightness-110 transition-all"
          data-testid="add-funds-banner-btn"
        >
          <Plus className="w-3 h-3" />
          Add Funds
        </button>
        <button
          onClick={() => setDismissed(true)}
          className={`p-1 rounded-md ${isDark ? 'hover:bg-white/10 text-white/30' : 'hover:bg-gray-100 text-gray-400'}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <AddFundsModal open={showFundsModal} onClose={() => setShowFundsModal(false)} />
    </>
  );
};

export default LowBalanceBanner;
